import { useContext, useEffect, useState } from "react";

import Typography from "@/design-system/Typography";
import ChevronBottom from "@/design-system/icons/chevron-bottom";
import PulseLoading from "@/design-system/pulse-loading";
import TelemetryButton from "@/design-system/telemetry-button";
import { FetchGet } from "@/lib/fetch-helper";
import { TelemetryEntity, sendTelemetryEvent } from "@/lib/telemetry";
import { AvailabilityState, CashbackComplaintStatus } from "@/src/cashback/CashbackCommonInterfaces";
import type { IComplaintNew } from "@/src/cashback/MainPage/ComplaintNew";
import ComplaintNew from "@/src/cashback/MainPage/ComplaintNew";
import StatusLabel from "@/src/cashback/MainPage/StatusLabel";
import TextLabel from "@/src/cashback/MainPage/TextLabel";
import { TransactionItemResources } from "@/src/cashback/MainPage/Transactions/TransactionItem.Resources";
import { TransactionItemResourcesEn } from "@/src/cashback/MainPage/Transactions/TransactionItem.Resources.En";
import type { ITransactionItem } from "@/src/cashback/MainPage/Transactions/shared";
import { TransactionsContext } from "@/src/cashback/MainPage/TransactionsContext";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { useMutationCustom } from "@/src/query-client-overrides";
import ImageLoader from "@Components/ImageLoader";
import * as stylex from "@stylexjs/stylex";

import StepCircleArrows from "@Assets/StepCircleArrows.svg";

import {
    ComposableSpacing,
    type QuerySmMax,
    colors,
    genericStyles,
    globalFontStyles,
} from "../../../../styles/tokens.stylex";

export default function TransactionItem(props: ITransactionItem) {
    const [isExtended, setIsExtended] = useState(false);
    const { isMobileOrTablet } = useViewportInfoContext();

    const { claim } = useContext(TransactionsContext);
    const onClaimClick = () => claim(props);

    const resources = languageSelector(TransactionItemResources, TransactionItemResourcesEn);

    const {
        mutate,
        data: complainList,
        isPending: isLoading,
    } = useMutationCustom({
        mutationKey: ["ComplaintList", props.id],
        mutationFn: () => FetchGet<IComplaintNew[]>(`/api/cashback/ComplaintList?id=${props.id}`),
        onSuccess: (data) => {
            if (data) {
                setIsExtended(true);
            }
        },
    });

    const isWithdrawal = props.availability === AvailabilityState.Unknown;

    const renderImage = () => {
        if (isWithdrawal) {
            return (
                <ImageLoader
                    styleX={[styles.gridLogo, styles.logoImg]}
                    src={props.icon || StepCircleArrows}
                    lazyLoading
                />
            );
        }

        return (
            <TelemetryButton
                as="a"
                size="M"
                href={props.isTemporaryPage ? undefined : props.brandNavigationUrl}
                styleX={[styles.gridLogo, styles.roundBackground]}
                itemId={props.brandId}
                eventName={`${TelemetryEntity.Cashback}-CashbackBrand}`}
                onClick={(ev) => {
                    ev.stopPropagation();
                }}
            >
                <ImageLoader styleX={[styles.gridLogo, styles.roundImage]} src={props.logo} lazyLoading />
            </TelemetryButton>
        );
    };

    const renderTrxInfo = (text: string) =>
        text && (
            <Typography textElement="p" styleX={styles.transactionInfo}>
                {text}
            </Typography>
        );

    const renderWithdrawalInfo = !isWithdrawal ? null : (
        <>
            {props.accountNumber && !props.hasCreditCard && (
                <TextLabel
                    id="transactionAccountNumber"
                    variant="blueBorder"
                    labelText={resources.accountNumberLabel}
                    text={props.accountNumber}
                    styleX={styles.withdrawalText}
                    hideIcon
                />
            )}
            {props.recipient && (
                <TextLabel
                    id="transactionRecipient"
                    variant="blueBorder"
                    labelText={resources.recipientLabel}
                    text={props.recipient}
                    styleX={[styles.withdrawalText, styles.withdrawalSpace]}
                    hideIcon
                />
            )}
        </>
    );

    const renderOrderInfo = (
        <div {...stylex.props(styles.gridOrderInfo)}>
            <Typography textElement="p" styleX={styles.boldText}>
                {props.brandName}
            </Typography>
            {!isMobileOrTablet && props.orderId && (
                <TextLabel
                    id="cashbackOrderId"
                    variant="gray"
                    text={props.orderId}
                    labelText="ID:"
                    styleX={globalFontStyles.BodyS}
                />
            )}
            {isMobileOrTablet && (
                <Typography textElement="p" styleX={styles.smallText}>
                    {props.date}
                </Typography>
            )}
            {props.complaintStatus !== CashbackComplaintStatus.Unknown && (
                <div {...stylex.props(styles.complaintStatus)}>
                    <Typography textElement="p" styleX={styles.complaintStatusLabel}>
                        {resources.complaintStatusLabel}
                    </Typography>
                    <StatusLabel
                        status={props.complaintStatus}
                        statusLabel={props.complaintStatusDescription}
                    />
                </div>
            )}
        </div>
    );

    const renderOrderInfoAndPurchaseAmount = (!!props.orderId ||
        !!props.conditionsToApproveDescription ||
        !!props.purchaseAmount) && (
        <div {...stylex.props(styles.orderInfoAndPurchaseAmount)}>
            {props.orderId && (
                <div>
                    {props.purchaseAmount && (
                        <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                            {resources.orderIdMobileLabel}
                        </Typography>
                    )}
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.orderId}
                    </Typography>
                </div>
            )}

            {props.conditionsToApproveDescription && (
                <div>
                    <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                        {resources.trxConditionsLabel}
                    </Typography>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.conditionsToApproveDescription}
                    </Typography>
                </div>
            )}

            {props.purchaseAmount && (
                <div>
                    <Typography textElement="span" fontStyle="BodyS" styleX={styles.label}>
                        {resources.purchaseAmountLabel}
                    </Typography>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.text}>
                        {props.purchaseAmount}
                    </Typography>
                    {props.deliveryCostInfo && (
                        <Typography textElement="span" fontStyle="BodyS" styleX={styles.smallLabel}>
                            {props.deliveryCostInfo}
                        </Typography>
                    )}
                </div>
            )}
        </div>
    );

    const onTransactionItemClick = () => {
        if (!isExtended && !complainList && !isWithdrawal) {
            mutate();
        }

        sendTelemetryEvent({
            eventName: `cshbck-${isWithdrawal ? "wthdrDetailsOpen" : "trxDetailsOpen"}`,
            actionType: "Click",
            itemId: props.id,
        });

        setIsExtended(!isExtended);
    };

    useEffect(() => {
        if (props.isExtended) {
            mutate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div {...stylex.props(styles.main, isExtended && styles.backgroundChange)}>
            <div {...stylex.props(styles.item)} onClick={onTransactionItemClick}>
                {renderImage()}
                {renderOrderInfo}
                <Typography
                    textElement="p"
                    fontStyle="BodyM"
                    styleX={[styles.gridDate, styles.boldText]}
                    onClick={(ev) => ev.preventDefault()}
                >
                    {props.date}
                </Typography>
                <div {...stylex.props(styles.gridAmount, styles.purchaseAmount)}>
                    {props.purchaseAmount && (
                        <>
                            <Typography textElement="p" fontStyle="BodyM" styleX={styles.boldText}>
                                {props.purchaseAmount}
                            </Typography>
                            {props.deliveryCostInfo && (
                                <Typography textElement="p" fontStyle="BodyS" styleX={styles.smallText}>
                                    {props.deliveryCostInfo}
                                </Typography>
                            )}
                        </>
                    )}
                </div>
                <div {...stylex.props(styles.gridAmountCb)}>
                    <Typography textElement="span" fontStyle="BodyM" styleX={styles.amountCB}>
                        {props.returnedAmount}
                    </Typography>
                    <span {...stylex.props(styles.arrow, isExtended && styles.arrowRotate)}>
                        <ChevronBottom />
                    </span>
                </div>
            </div>

            <div {...stylex.props(styles.extraInfo, isExtended && styles.extraInfoOpen)}>
                <div {...stylex.props(styles.innerExtraInfo)}>
                    {renderOrderInfoAndPurchaseAmount}
                    {props.purchaseAmount && renderTrxInfo(props.transactionInfo)}
                    {props.availability === AvailabilityState.Rejected &&
                        renderTrxInfo(props.rejectionReasonTypeDescription)}
                    {isLoading ? (
                        <PulseLoading
                            pulseNumber={2}
                            styleX={styles.loading}
                            stylexItem={styles.loadingItem}
                        />
                    ) : (
                        complainList &&
                        complainList.map((val, index) => (
                            <ComplaintNew {...val} key={index} styleX={styles.complaintNewContainer} />
                        ))
                    )}
                    {props.canCreateComplaint && (
                        <TelemetryButton
                            as="button"
                            variant="magentaRound"
                            size="M"
                            onClick={onClaimClick}
                            styleX={styles.createComplaintBtn}
                            eventName="cshbck-CashbackBrand"
                            itemId={props.brandId}
                        >
                            {complainList?.length > 0 ? resources.btnTextAgain : resources.btnText}
                        </TelemetryButton>
                    )}
                    {renderWithdrawalInfo}
                </div>
            </div>
        </div>
    );
}

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    main: {
        borderBottomColor: colors.b_Primary,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
    },

    item: {
        padding: ComposableSpacing.s3,
        gap: ComposableSpacing.s3,
        alignItems: "center",
        backgroundColor: { default: null, ":hover": "#fff" },
        cursor: "pointer",
        display: "grid",
        gridTemplateColumns: "60px auto 80px calc(33% - 100px) calc(33% - 100px)",
        gridTemplateRows: "1fr",
        transitionDuration: "300ms",
        transitionProperty: "background-color",
        transitionTimingFunction: "ease",
    },

    backgroundChange: {
        backgroundColor: "#fff",
    },

    gridLogo: {
        gridColumn: "1/2",
    },

    gridOrderInfo: {
        gridColumn: "2/3",
        alignItems: "flex-start",
        display: "inline-flex",
        flexDirection: "column",
        justifyContent: "center",
    },

    gridDate: {
        gridColumn: "3/4",
    },

    gridAmount: {
        gridColumn: "4/5",
    },

    gridAmountCb: {
        gap: ComposableSpacing.s2,
        gridColumn: "5/6",
        display: "flex",
        justifySelf: "end",
    },

    logoImg: {
        padding: 0,
        height: 38,
        width: 38,
    },

    roundBackground: {
        padding: 0,
        borderColor: colors.b_centas,
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: 1,
        backgroundColor: "white",
        display: "block",
        height: 60,
        width: 60,
    },

    roundImage: {
        padding: 0,
        height: "100%",
        width: "100%",
    },

    boldText: {
        fontWeight: "bold",
        paddingBottom: ComposableSpacing.s1,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: ComposableSpacing.s1,
    },

    smallText: {
        color: colors.grayInactive,
    },

    complaintStatus: {
        gap: ComposableSpacing.s1,
        alignItems: "center",
        display: "inline-flex",
        paddingTop: ComposableSpacing.s1,
    },

    complaintStatusLabel: {
        fontSize: ".75rem",
    },

    purchaseAmount: {
        display: "flex",

        flexDirection: "column",
        textAlign: "center",
    },

    complaintNewContainer: {
        borderBottomColor: colors.fg_Tertiary,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
    },

    createComplaintBtn: {
        margin: 10,
        fontSize: ".75rem",
        justifySelf: "end",
    },

    transactionInfo: {
        fontSize: ".75rem",
        fontWeight: "bold",
    },

    extraInfo: {
        overflow: "hidden",
        backgroundColor: "white",
        display: "grid",
        gridTemplateRows: "0fr",

        transitionDuration: "300ms",
        transitionProperty: "grid-template-rows, opacity",
        transitionTimingFunction: "ease-out",
        paddingBottom: { default: 0, [smMax]: ComposableSpacing.s2 },
        paddingLeft: { default: 70, [smMax]: ComposableSpacing.s2 },
        paddingRight: ComposableSpacing.s2,
        paddingTop: 0,
    },

    extraInfoOpen: {
        gridTemplateRows: "1fr",
    },

    innerExtraInfo: {
        gap: ComposableSpacing.s2,
        overflow: "hidden",
        display: "grid",
    },

    orderInfoAndPurchaseAmount: {
        color: "#242424",
        paddingBottom: "0.875rem",
    },

    label: {
        color: colors.fg_Tertiary,
        fontWeight: 400,
        textTransform: "uppercase",
        marginBottom: 0,
    },

    text: {
        display: "block",
        fontSize: ".875rem",
        fontWeight: "bold",
        wordBreak: "break-word",
    },

    smallLabel: {
        margin: { default: null, [smMax]: "-3px 0 0 0" },
        color: colors.fg_Tertiary,
        display: { default: "unset", [smMax]: "block" },
    },

    amountCB: {
        fontWeight: "bold",
        textAlign: "right",
    },

    arrow: {
        strokeWidth: 4,
        color: colors.b_BrandMagenta,
        display: "inline-block",
        flexShrink: 0,
        transitionDuration: "180ms",
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        verticalAlign: "sub",
        height: 20,
        width: 20,
    },

    arrowRotate: {
        transform: "rotate(180deg)",
    },

    loading: {
        gap: ComposableSpacing.s3,
    },

    loadingItem: {
        borderRadius: ComposableSpacing.s1,
        height: ComposableSpacing.s4,
        width: { default: "40%", [":first-child"]: "25%" },
    },

    withdrawalText: {
        fontSize: ".75rem",
    },

    withdrawalSpace: {
        paddingBottom: ComposableSpacing.s3,
    },
});
